/**
 * Free-to-Play tier shared constants (Phase 2). Free starters are the Common-rarity
 * species — a bounded surface with real gameplay. Both backend (freeAdoptionService)
 * and web (adoption UI) import this single source of truth.
 * Spec: docs/superpowers/specs/free-to-play-tier.md
 */
import { SUIKAMON_SPECIES } from './speciesConfig.js';
import { Rarity } from './petModels.js';

/** Free starters are the Common-rarity species, plus the few {@link FREE_STARTER_EXTRA_NAMES}. */
export const FREE_STARTER_RARITY = Rarity.Common;

/**
 * Non-Common species that are ALSO free starters. Aqua is here so the first screen can offer a
 * Water starter beside Fire and Nature — every Common is Nature or Fire, and the owner wanted the
 * best first impression (2026-10-24). Hotpaw is here because the owner made it the featured
 * Fire starter in Talion's place (2026-10-25). Still one free starter per account: this widens
 * what you may pick, never how many.
 */
export const FREE_STARTER_EXTRA_NAMES: readonly string[] = ['Aqua', 'Hotpaw'];

/** Whether a species can be adopted free. The ONE rule — never compare rarity by hand. */
export function isFreeStarterSpecies(species: { rarity: Rarity; name: string }): boolean {
  return species.rarity === FREE_STARTER_RARITY || FREE_STARTER_EXTRA_NAMES.includes(species.name);
}

/** Species eligible for free adoption — derived, never hardcoded ids. */
export function getFreeStarterSpecies() {
  return SUIKAMON_SPECIES.filter(isFreeStarterSpecies);
}

/**
 * The starters the first screen OFFERS — three, like a classic starter trio, because six cards
 * read as a shop shelf and made a first-time visitor compare instead of choose. Picked for three
 * silhouettes, three colours and three elements a child tells apart at a glance: a blue Water
 * dragon-pup, a Fire Hotpaw and a purple Nature bat. The other free starters are still adoptable: a species page
 * or the home hero that links to one (`/play-free?species=…`) adds it to the line-up.
 */
export const FEATURED_STARTER_NAMES = ['Aqua', 'Hotpaw', 'Pefareon'] as const;

/** The featured starters, resolved from the released free roster (never hardcoded ids). */
export function getFeaturedStarterSpecies() {
  const released = getFreeStarterSpecies().filter((s) => s.available);
  const featured = FEATURED_STARTER_NAMES
    .map((name) => released.find((s) => s.name === name))
    .filter((s): s is (typeof released)[number] => s !== undefined);
  // A renamed or retired starter must never leave the first screen empty.
  return featured.length > 0 ? featured : released;
}
